import { apiClient } from '../../../lib/apiClient';

// ── Types ──────────────────────────────────────────────────────────────────────
export interface Supplier {
  id: number;
  name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  address?: string;
  nit?: string;
  notes?: string;
  active: boolean;
}

export interface PurchaseOrderItem {
  id?: number;
  inventory_item_id: number;
  quantity: number;
  unit: string;
  unit_price: number;
  line_total?: number;
  inventoryItem?: { id: number; name: string; unit: string };
}

export interface PurchaseOrder {
  id: number;
  supplier_id: number;
  invoice_number?: string;
  purchased_at: string;
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  stock_applied: boolean;
  items_count?: number;
  supplier?: Supplier;
  items?: PurchaseOrderItem[];
}

export interface PriceHistoryEntry {
  date: string;
  supplier: string;
  supplier_id: number;
  invoice_number?: string;
  unit_price: number;
  unit: string;
  quantity: number;
  price_change: number | null;
  trend: 'up' | 'down' | 'stable' | 'neutral';
}

export interface PriceHistoryResult {
  history: PriceHistoryEntry[];
  stats: {
    min: number; max: number; avg: number;
    last: number; count: number; total_change: number;
  };
}

export interface PriceOverviewItem {
  inventory_item_id: number;
  item_name: string;
  item_unit: string;
  purchase_count: number;
  last_purchase_at: string;
  stats: PriceHistoryResult['stats'];
  last_entries: PriceHistoryEntry[];
}

// ── Suppliers ──────────────────────────────────────────────────────────────────
export const listSuppliers = async (): Promise<Supplier[]> =>
  (await apiClient.get<Supplier[]>('purchases/suppliers')).data;

export const createSupplier = async (data: Partial<Supplier>): Promise<Supplier> =>
  (await apiClient.post<Supplier>('purchases/suppliers', data)).data;

export const updateSupplier = async (id: number, data: Partial<Supplier>): Promise<Supplier> =>
  (await apiClient.patch<Supplier>(`purchases/suppliers/${id}`, data)).data;

export const deleteSupplier = async (id: number): Promise<void> =>
  (await apiClient.delete(`purchases/suppliers/${id}`)).data;

// ── Purchase Orders ────────────────────────────────────────────────────────────
export const listOrders = async (params?: Record<string, unknown>) =>
  (await apiClient.get<{ data: PurchaseOrder[]; total: number }>('purchases/orders', { params })).data;

export const getOrder = async (id: number): Promise<PurchaseOrder> =>
  (await apiClient.get<PurchaseOrder>(`purchases/orders/${id}`)).data;

export const createOrder = async (data: {
  supplier_id: number;
  invoice_number?: string;
  purchased_at: string;
  tax?: number;
  notes?: string;
  apply_stock?: boolean;
  items: Omit<PurchaseOrderItem, 'id' | 'line_total' | 'inventoryItem'>[];
}): Promise<PurchaseOrder> =>
  (await apiClient.post<PurchaseOrder>('purchases/orders', data)).data;

export const applyStock = async (id: number): Promise<void> =>
  (await apiClient.post(`purchases/orders/${id}/apply-stock`)).data;

export const deleteOrder = async (id: number): Promise<void> =>
  (await apiClient.delete(`purchases/orders/${id}`)).data;


// ── Price History ──────────────────────────────────────────────────────────────
export const getPriceHistory = async (inventoryItemId: number, params?: Record<string, unknown>): Promise<PriceHistoryResult> =>
  (await apiClient.get<PriceHistoryResult>(`purchases/price-history/${inventoryItemId}`, { params })).data;

export const getPriceOverview = async (): Promise<PriceOverviewItem[]> =>
  (await apiClient.get<PriceOverviewItem[]>('purchases/price-overview')).data;
